import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";
import { Field, type FieldSize } from "./Field";
import "./Input.css";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: FieldSize;
  /** Leading decorative icon (e.g. a search glass). */
  icon?: ReactNode;
  /** Class for the outer field wrapper; `className` goes on the <input>. */
  fieldClassName?: string;
}

/** Text input with label, hint and error wiring (aria-describedby / aria-invalid). */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, size = "md", icon, id, required, disabled, className, fieldClassName, ...rest },
  ref,
) {
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      id={id}
      required={required}
      disabled={disabled}
      className={fieldClassName}
    >
      {({ controlId, describedBy }) => (
        <div className={cx("ui-input", icon ? "ui-input--with-icon" : false)}>
          {icon ? (
            <span className="ui-input__icon" aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <input
            ref={ref}
            id={controlId}
            className={cx("ui-control", `ui-control--${size}`, "ui-input__control", className)}
            required={required}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...rest}
          />
        </div>
      )}
    </Field>
  );
});
